import React, { useState, useRef, useCallback } from 'react';
import {
  X,
  Camera,
  Upload,
  Send,
  Bug,
  MessageSquare,
  AlertTriangle,
  Info,
  Check
} from 'lucide-react';
import html2canvas from 'html2canvas';
import Cookies from 'js-cookie';
import { submitBugReport } from '../../apis/bug-report.api';
import DataLayer from '../../utils/data-layer';

const BugReportModal = ({ isOpen, onClose }) => {
  const [step, setStep] = useState(1); // 1: Type selection, 2: Report form, 3: Success
  const [reportType, setReportType] = useState('');
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    priority: 'medium',
    email: '',
    isAnonymous: false,
    screenshots: []
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [uploadProgress, setUploadProgress] = useState({});
  const [dragOver, setDragOver] = useState(false);
  const fileInputRef = useRef(null);
  const token = Cookies.get('token');
  const isLoggedIn = !!token;

  const resetModal = useCallback(() => {
    setStep(1);
    setReportType('');
    setFormData({
      title: '',
      description: '',
      priority: 'medium',
      email: '',
      isAnonymous: false,
      screenshots: []
    });
    setIsSubmitting(false);
    setUploadProgress({});
    setDragOver(false);
  }, []);

  const handleClose = useCallback(() => {
    onClose();
    setTimeout(resetModal, 300); // Reset after modal animation
  }, [onClose, resetModal]);

  const takeScreenshot = useCallback(async () => {
    try {
      // Find the modal container and backdrop
      const modalContainer = document.querySelector('[class*="fixed inset-0"]');
      const modalBackdrop = modalContainer?.parentElement;

      // Temporarily hide the modal and backdrop
      if (modalContainer) {
        modalContainer.style.display = 'none';
      }

      // Wait a brief moment for the DOM to update
      await new Promise((resolve) => setTimeout(resolve, 100));

      // Take screenshot of the page without modal
      const canvas = await html2canvas(document.body, {
        height: window.innerHeight,
        width: window.innerWidth,
        scrollX: 0,
        scrollY: 0,
        useCORS: true,
        allowTaint: true,
        ignoreElements: (element) => {
          // Ignore any remaining modal elements
          return element.classList.contains('fixed') && element.classList.contains('inset-0');
        }
      });

      // Show the modal again
      if (modalContainer) {
        modalContainer.style.display = '';
      }

      canvas.toBlob((blob) => {
        const file = new File([blob], `screenshot-${Date.now()}.png`, { type: 'image/png' });
        setFormData((prev) => ({
          ...prev,
          screenshots: [...prev.screenshots, file]
        }));
      }, 'image/png');
    } catch (error) {
      console.error('Failed to take screenshot:', error);
      // Make sure modal is visible again even if screenshot fails
      const modalContainer = document.querySelector('[class*="fixed inset-0"]');
      if (modalContainer) {
        modalContainer.style.display = '';
      }
      alert('Failed to take screenshot. Please try uploading an image instead.');
    }
  }, []);

  const handleFileUpload = useCallback((files) => {
    const imageFiles = Array.from(files).filter(
      (file) => file.type.startsWith('image/') && file.size <= 10 * 1024 * 1024 // 10MB limit
    );

    if (imageFiles.length !== files.length) {
      alert('Only image files under 10MB are allowed.');
    }

    setFormData((prev) => ({
      ...prev,
      screenshots: [...prev.screenshots, ...imageFiles].slice(0, 5) // Max 5 images
    }));
  }, []);

  const handleDrop = useCallback(
    (e) => {
      e.preventDefault();
      setDragOver(false);
      handleFileUpload(e.dataTransfer.files);
    },
    [handleFileUpload]
  );

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    setDragOver(true);
  }, []);

  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    setDragOver(false);
  }, []);

  const removeScreenshot = useCallback((index) => {
    setFormData((prev) => ({
      ...prev,
      screenshots: prev.screenshots.filter((_, i) => i !== index)
    }));
  }, []);

  const handleSubmit = useCallback(
    async (e) => {
      e.preventDefault();
      if (!formData.title.trim() || !formData.description.trim()) {
        alert('Please fill in both title and description.');
        return;
      }

      setIsSubmitting(true);

      try {
        // Prepare data for submission
        const metadata = {
          viewport: {
            width: window.innerWidth,
            height: window.innerHeight
          },
          screen: {
            width: window.screen.width,
            height: window.screen.height
          },
          timestamp: new Date().toISOString(),
          referrer: document.referrer
        };

        const apiData = {
          type: reportType,
          title: formData.title,
          description: formData.description,
          priority: formData.priority,
          email: formData.email,
          isAnonymous: formData.isAnonymous,
          url: window.location.href,
          userAgent: navigator.userAgent,
          metadata,
          screenshots: formData.screenshots
        };

        const response = await submitBugReport(apiData);

        if (response) {
          DataLayer.trackFeedback(reportType);
          setStep(3); // Success step
        } else {
          throw new Error('Failed to submit report');
        }
      } catch (error) {
        console.error('Error submitting bug report:', error);
        const errorMessage =
          error?.response?.data?.message || 'Failed to submit report. Please try again.';
        alert(errorMessage);
      } finally {
        setIsSubmitting(false);
      }
    },
    [reportType, formData]
  );

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[1000] p-4">
      <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-hidden shadow-2xl">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 bg-gradient-to-r from-blue-50 to-green-50">
          <div className="flex items-center gap-3">
            {reportType === 'bug' ? (
              <Bug className="w-6 h-6 text-red-500" />
            ) : reportType === 'feedback' ? (
              <MessageSquare className="w-6 h-6 text-green-500" />
            ) : (
              <Info className="w-6 h-6 text-blue-500" />
            )}
            <h2 className="text-xl font-semibold text-gray-800">
              {step === 1 && 'Help Us Improve Twygs'}
              {step === 2 && (reportType === 'bug' ? 'Report a Bug' : 'Share Feedback')}
              {step === 3 && 'Thank You!'}
            </h2>
          </div>
          <button
            onClick={handleClose}
            className="p-2 hover:bg-gray-100 rounded-full transition-colors"
          >
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        <div className="overflow-y-auto max-h-[calc(90vh-80px)]">
          {/* Step 1: Type Selection */}
          {step === 1 && (
            <div className="p-6">
              <div className="text-center mb-8">
                <p className="text-gray-600 text-lg mb-2">
                  We're in beta and your input is invaluable!
                </p>
                <p className="text-gray-500">
                  Help us build a better platform by reporting issues or sharing your thoughts.
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <button
                  onClick={() => {
                    setReportType('bug');
                    setStep(2);
                  }}
                  className="p-6 border-2 border-gray-200 rounded-xl hover:border-red-300 hover:bg-red-50 transition-all duration-200 text-left group"
                >
                  <div className="flex items-center gap-3 mb-3">
                    <Bug className="w-8 h-8 text-red-500 group-hover:scale-110 transition-transform" />
                    <h3 className="text-lg font-semibold text-gray-800">Report a Bug</h3>
                  </div>
                  <p className="text-gray-600">
                    Something not working as expected? Let us know about technical issues, errors,
                    or broken features.
                  </p>
                </button>

                <button
                  onClick={() => {
                    setReportType('feedback');
                    setStep(2);
                  }}
                  className="p-6 border-2 border-gray-200 rounded-xl hover:border-green-300 hover:bg-green-50 transition-all duration-200 text-left group"
                >
                  <div className="flex items-center gap-3 mb-3">
                    <MessageSquare className="w-8 h-8 text-green-500 group-hover:scale-110 transition-transform" />
                    <h3 className="text-lg font-semibold text-gray-800">Share Feedback</h3>
                  </div>
                  <p className="text-gray-600">
                    Have ideas for improvements? Want to suggest new features? We'd love to hear
                    your thoughts!
                  </p>
                </button>
              </div>
            </div>
          )}

          {/* Step 2: Report Form */}
          {step === 2 && (
            <form onSubmit={handleSubmit} className="p-6 space-y-6">
              {/* Anonymous Toggle */}
              {!isLoggedIn && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <div className="flex items-center gap-3">
                    <input
                      type="checkbox"
                      id="anonymous"
                      checked={formData.isAnonymous}
                      onChange={(e) =>
                        setFormData((prev) => ({ ...prev, isAnonymous: e.target.checked }))
                      }
                      className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
                    />
                    <label htmlFor="anonymous" className="text-sm font-medium text-gray-700">
                      Submit anonymously
                    </label>
                  </div>
                  <p className="text-xs text-gray-600 mt-1">
                    We won't collect any identifying information about you.
                  </p>
                </div>
              )}

              {/* Email for anonymous users */}
              {!isLoggedIn && !formData.isAnonymous && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Email (optional)
                  </label>
                  <input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData((prev) => ({ ...prev, email: e.target.value }))}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="your@email.com"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    We'll only use this to follow up if needed.
                  </p>
                </div>
              )}

              {/* Priority for bugs */}
              {reportType === 'bug' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Priority Level
                  </label>
                  <select
                    value={formData.priority}
                    onChange={(e) => setFormData((prev) => ({ ...prev, priority: e.target.value }))}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="low">Low - Minor issue, doesn't affect usage</option>
                    <option value="medium">Medium - Noticeable issue, workaround available</option>
                    <option value="high">
                      High - Significant issue, affects key functionality
                    </option>
                    <option value="critical">Critical - Blocks usage, urgent fix needed</option>
                  </select>
                </div>
              )}

              {/* Title */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Title *</label>
                <input
                  type="text"
                  value={formData.title}
                  onChange={(e) => setFormData((prev) => ({ ...prev, title: e.target.value }))}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder={
                    reportType === 'bug'
                      ? 'Brief description of the bug'
                      : 'Summary of your feedback'
                  }
                  required
                />
              </div>

              {/* Description */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Description *
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) =>
                    setFormData((prev) => ({ ...prev, description: e.target.value }))
                  }
                  rows={6}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                  placeholder={
                    reportType === 'bug'
                      ? 'Please describe:\n• What you were trying to do\n• What happened instead\n• Steps to reproduce the issue\n• Any error messages you saw'
                      : "Tell us about your experience:\n• What do you like?\n• What could be improved?\n• Any specific features you'd like to see?\n• General thoughts and suggestions"
                  }
                  required
                />
              </div>

              {/* Screenshots */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Screenshots (optional)
                </label>

                {/* Screenshot actions */}
                <div className="flex flex-wrap gap-2 mb-3">
                  <button
                    type="button"
                    onClick={takeScreenshot}
                    className="flex items-center gap-2 px-3 py-2 bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200 transition-colors text-sm"
                  >
                    <Camera className="w-4 h-4" />
                    Take Screenshot
                  </button>

                  <button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    className="flex items-center gap-2 px-3 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors text-sm"
                  >
                    <Upload className="w-4 h-4" />
                    Upload Images
                  </button>
                </div>

                {/* Drop zone */}
                <div
                  onDrop={handleDrop}
                  onDragOver={handleDragOver}
                  onDragLeave={handleDragLeave}
                  className={`border-2 border-dashed rounded-lg p-6 text-center transition-colors ${
                    dragOver
                      ? 'border-blue-400 bg-blue-50'
                      : 'border-gray-300 hover:border-gray-400'
                  }`}
                >
                  <Upload className="w-8 h-8 text-gray-400 mx-auto mb-2" />
                  <p className="text-sm text-gray-600">
                    Drag and drop images here, or click "Upload Images" above
                  </p>
                  <p className="text-xs text-gray-500 mt-1">Max 5 images, 10MB each</p>
                </div>

                <input
                  ref={fileInputRef}
                  type="file"
                  multiple
                  accept="image/*"
                  onChange={(e) => handleFileUpload(e.target.files)}
                  className="hidden"
                />

                {/* Screenshot previews */}
                {formData.screenshots.length > 0 && (
                  <div className="mt-4">
                    <p className="text-sm font-medium text-gray-700 mb-2">
                      Attached Images ({formData.screenshots.length}/5)
                    </p>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                      {formData.screenshots.map((file, index) => (
                        <div key={index} className="relative group">
                          <img
                            src={URL.createObjectURL(file)}
                            alt={`Screenshot ${index + 1}`}
                            className="w-full h-20 object-cover rounded border"
                          />
                          {uploadProgress[index] && (
                            <div className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center rounded">
                              <div className="text-white text-xs">
                                {uploadProgress[index] === 100 ? '✓' : `${uploadProgress[index]}%`}
                              </div>
                            </div>
                          )}
                          <button
                            type="button"
                            onClick={() => removeScreenshot(index)}
                            className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white rounded-full flex items-center justify-center text-xs opacity-0 group-hover:opacity-100 transition-opacity"
                            disabled={uploadProgress[index] && uploadProgress[index] < 100}
                          >
                            <X className="w-3 h-3" />
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* Submit button */}
              <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
                <button
                  type="button"
                  onClick={() => setStep(1)}
                  className="px-6 py-2 text-gray-600 hover:text-gray-800 transition-colors"
                >
                  Back
                </button>
                <button
                  type="submit"
                  disabled={isSubmitting || !formData.title.trim() || !formData.description.trim()}
                  className="flex items-center gap-2 px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {isSubmitting ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      {Object.keys(uploadProgress).length > 0 ? 'Uploading...' : 'Submitting...'}
                    </>
                  ) : (
                    <>
                      <Send className="w-4 h-4" />
                      Submit {reportType === 'bug' ? 'Bug Report' : 'Feedback'}
                    </>
                  )}
                </button>
              </div>
            </form>
          )}

          {/* Step 3: Success */}
          {step === 3 && (
            <div className="p-8 text-center">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Check className="w-8 h-8 text-green-600" />
              </div>
              <h3 className="text-xl font-semibold text-gray-800 mb-2">Thank You!</h3>
              <p className="text-gray-600 mb-6">
                Your {reportType === 'bug' ? 'bug report' : 'feedback'} has been submitted
                successfully. We appreciate you helping us improve Twygs!
              </p>
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
                <p className="text-sm text-blue-800">
                  <strong>What happens next?</strong>
                  <br />
                  Our team will review your submission and may reach out if we need additional
                  information.
                  {reportType === 'bug' &&
                    ' Bug fixes are prioritized based on severity and impact.'}
                </p>
              </div>
              <button
                onClick={handleClose}
                className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                Close
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default BugReportModal;
